import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmationComponent } from '../../shared/confirmation/confirmation.component';
import { AdminService } from '../../shared/services/admin.service';
import { ToastService } from '../../shared/toast/toast.service';
import { ApplicationConfigurationDto } from './dto/application-configuration.dto';
import { HomeMessageDto } from './dto/home.message.dto';

@Component({
  selector: 'app-admin-general',
  templateUrl: './admin-general.component.html',
  styleUrls: ['./admin-general.component.scss'],
  standalone: false,
})
export class AdminGeneralComponent implements OnInit, OnDestroy {
  public formGroup: FormGroup<{
    textValue: FormControl<string | null>;
    maintenance: FormControl<boolean | null>;
  }>;

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly adminService: AdminService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
  ) {}

  public async ngOnInit(): Promise<void> {
    const messageDto: HomeMessageDto = await this.adminService.getHomeMessage();
    const applicationConfigurationDto: ApplicationConfigurationDto = await this.adminService.getApplicationConfiguration();
    this.formGroup = new FormGroup({
      textValue: new FormControl<string | null>(messageDto?.message),
      maintenance: new FormControl<boolean | null>(applicationConfigurationDto?.maintenance),
    });
    this.formGroup.controls.maintenance.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: boolean) => this.updateMaintenanceMode(value));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async onSubmit(): Promise<void> {
    await this.adminService.setHomeMessage(this.formGroup.controls.textValue.value);
    this.toastService.show('Le message a été modifié avec succès.', 'success');
  }

  public async updateMaintenanceMode(value: boolean): Promise<void> {
    if (value) {
      const confirm: boolean = await this.openConfirmationModal();
      if (confirm) {
        await this.adminService.setApplicationConfiguration({
          maintenance: value,
        } as ApplicationConfigurationDto);
        this.toastService.show('Le mode maintenance est désormais actif.', 'success');
      } else {
        this.formGroup.controls.maintenance.setValue(!value, { emitEvent: false });
      }
    } else {
      await this.adminService.setApplicationConfiguration({ maintenance: value } as ApplicationConfigurationDto);
      this.toastService.show('Le mode maintenance est désormais inactif.', 'success');
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm(`Êtes-vous certain de vouloir passer Insertis en mode maintenance ?
        Tous les comptes non administrateur seront déconnectés et ne pourront plus se connecter tant que le mode maintenance est activé.`);
  }
}
